import axios, { AxiosError } from 'axios'

// ─── Axios Instance ──────────────────────────────────────────────────────────
export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  withCredentials: true, // httpOnly cookie auth
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
})

// ─── Response Interceptor ────────────────────────────────────────────────────
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Let the auth hook handle the redirect
      window.dispatchEvent(new CustomEvent('insulis:unauthorized'))
    }
    return Promise.reject(error)
  }
)

// ─── Types ───────────────────────────────────────────────────────────────────

export type TrendDirection =
  | 'rising_rapidly'
  | 'rising'
  | 'rising_slightly'
  | 'steady'
  | 'falling_slightly'
  | 'falling'
  | 'falling_rapidly'
  | 'unknown'

export type GlucoseRange =
  | 'urgent_low'
  | 'low'
  | 'in_range'
  | 'elevated'
  | 'high'
  | 'urgent_high'

export interface GlucoseReading {
  id: string
  timestamp: string // ISO 8601
  value_mgdl: number
  value_mmol: number
  trend_direction: TrendDirection
  range: GlucoseRange
  is_peak?: boolean
}

export interface CurrentGlucose {
  reading: GlucoseReading
  minutes_ago: number
  is_fresh: boolean // < 6 min old
}

export interface Peak {
  id: string
  timestamp: string
  value_mgdl: number
  value_mmol: number
  prominence: number // mg/dL above surrounding
  duration_minutes: number
  range: GlucoseRange
  meal_associated?: boolean
}

export interface TimeInRange {
  in_range_pct: number
  elevated_pct: number
  high_pct: number
  low_pct: number
  urgent_low_pct: number
  urgent_high_pct: number
}

export interface DailyInsights {
  date: string
  avg_mgdl: number
  avg_mmol: number
  tir: TimeInRange
  reading_count: number
  estimated_a1c: number
  peak_count: number
  highest_peak_mgdl: number | null
  avg_peak_duration: number | null
}

export interface InsulinSummary {
  date: string
  total_iu: number
  budget_iu: number
  remaining_iu: number
  pct_used: number
  over_budget: boolean
}

export interface User {
  id: string
  username: string
  email: string
  dexcom_connected: boolean
  dexcom_last_sync: string | null
  preferences: {
    theme: 'system' | 'light' | 'dark'
    target_low: number
    target_high: number
    insulin_budget: number
  }
}

// ─── Auth API ────────────────────────────────────────────────────────────────

export const auth = {
  login: async (email: string, password: string): Promise<User> => {
    const { data } = await api.post<User>('/auth/login', { username: email, password })
    return data
  },

  logout: async (): Promise<void> => {
    await api.post('/auth/logout')
  },

  me: async (): Promise<User> => {
    const { data } = await api.get<User>('/auth/me')
    return data
  },
}

// ─── Readings API ────────────────────────────────────────────────────────────

export const readings = {
  getCurrent: async (): Promise<CurrentGlucose> => {
    const { data } = await api.get<CurrentGlucose>('/readings/current')
    return data
  },

  getReadings: async (
    period: 'today' | 'week' | 'month'
  ): Promise<GlucoseReading[]> => {
    const { data } = await api.get<GlucoseReading[]>('/readings', {
      params: { period },
    })
    return data
  },

  getRange: async (
    from: string,
    to: string
  ): Promise<GlucoseReading[]> => {
    const { data } = await api.get<GlucoseReading[]>('/readings/range', {
      params: { from, to },
    })
    return data
  },
}

// ─── Peaks API ───────────────────────────────────────────────────────────────

export const peaks = {
  getPeaks: async (
    period?: 'today' | 'week' | 'month' | 'all'
  ): Promise<Peak[]> => {
    const { data } = await api.get<Peak[]>('/peaks', {
      params: { period: period ?? 'all' },
    })
    return data
  },

  getToday: async (): Promise<Peak[]> => {
    const { data } = await api.get<Peak[]>('/peaks/today')
    return data
  },
}

// ─── Insights API ────────────────────────────────────────────────────────────

export const insights = {
  getDaily: async (date?: string): Promise<DailyInsights> => {
    const { data } = await api.get<DailyInsights>('/insights/daily', {
      params: { date },
    })
    return data
  },

  getRange: async (
    period: 'today' | 'week' | 'month'
  ): Promise<DailyInsights[]> => {
    const { data } = await api.get<DailyInsights[]>('/insights/range', {
      params: { period },
    })
    return data
  },

  getInsulin: async (date?: string): Promise<InsulinSummary> => {
    const { data } = await api.get<InsulinSummary>('/insights/insulin', {
      params: { date },
    })
    return data
  },
}

// ─── Helper: mg/dL to mmol/L ─────────────────────────────────────────────────
export const mgdlToMmol = (mgdl: number): string =>
  (mgdl / 18.0182).toFixed(1)

// ─── Helper: Trend Arrow Character ───────────────────────────────────────────
export const trendArrow = (direction: TrendDirection): string => {
  const map: Record<TrendDirection, string> = {
    rising_rapidly: '↑↑',
    rising: '↑',
    rising_slightly: '↗',
    steady: '→',
    falling_slightly: '↘',
    falling: '↓',
    falling_rapidly: '↓↓',
    unknown: '–',
  }
  return map[direction]
}

// ─── Helper: Glucose Range Color CSS Variable ─────────────────────────────────
export const rangeColor = (range: GlucoseRange): string => {
  const map: Record<GlucoseRange, string> = {
    urgent_low: 'var(--glucose-urgent)',
    low: 'var(--glucose-low)',
    in_range: 'var(--glucose-in-range)',
    elevated: 'var(--glucose-elevated)',
    high: 'var(--glucose-high)',
    urgent_high: 'var(--glucose-urgent)',
  }
  return map[range]
}

// ─── Helper: Range Label ──────────────────────────────────────────────────────
export const rangeLabel = (range: GlucoseRange): string => {
  const map: Record<GlucoseRange, string> = {
    urgent_low: 'Urgent Low',
    low: 'Low',
    in_range: 'In Range',
    elevated: 'Elevated',
    high: 'High',
    urgent_high: 'Urgent High',
  }
  return map[range]
}

// ─── Estimated A1C Formula ────────────────────────────────────────────────────
export const estimateA1C = (avgMgdl: number): number =>
  Math.round(((avgMgdl / 28.7) + 2.15) * 10) / 10
